<template>
  <div class="create-customer-container mx-lg-3 mx-1">
    <div class="form-panel">
      <div class="panel-header">
        <div class="d-flex align-items-center">
          <i class="fas fa-user-plus me-3 header-icon"></i>
          <div>
            <h2 class="panel-title">Crear Nueva Cuenta de Cliente</h2>
            <p class="panel-subtitle">Complete el formulario para crear una nueva cuenta de cliente</p>
          </div>
        </div>
      </div>
      
      <div class="panel-body">
        <form @submit.prevent="handleSubmit" class="needs-validation" novalidate>
          <!-- Progress bar -->
          <div class="progress-container mb-4">
            <div class="progress" style="height: 6px;">
              <div 
                class="progress-bar" 
                role="progressbar" 
                :style="{ width: formProgress + '%' }"
                :class="{ 'bg-success': formProgress === 100 }"
              ></div>
            </div>
            <small class="text-muted mt-1 d-block text-end">
              {{ formProgress }}% completado
            </small>
          </div>

          <!-- Nombre de usuario -->
          <div class="form-group mb-4">
            <label for="username" class="form-label">
              Nombre de usuario
              <span class="text-danger">*</span>
            </label>
            <div class="input-group input-group-merge">
              <span class="input-group-text border-end-0">
                <i class="fas fa-user"></i>
              </span>
              <input
                type="text"
                class="form-control border-start-0"
                :class="{ 
                  'is-invalid': v$.username.$error,
                  'is-valid': formData.username && !v$.username.$error
                }"
                id="username"
                v-model="formData.username"
                placeholder="Ingrese nombre de usuario"
                @focus="fieldFocus = 'username'"
                @blur="fieldFocus = null"
              />
              <div class="invalid-feedback" v-if="v$.username.$error">
                {{ v$.username.$errors[0].$message }}
              </div>
              <div class="valid-feedback" v-if="formData.username && !v$.username.$error">
                ¡Nombre de usuario válido!
              </div>
            </div>
            <small 
              class="form-text" 
              :class="{ 'text-muted': fieldFocus !== 'username', 'text-primary': fieldFocus === 'username' }"
            >
              El nombre de usuario debe tener al menos 4 caracteres
            </small>
          </div>

          <!-- Email -->
          <div class="form-group mb-4">
            <label for="email" class="form-label">
              Correo electrónico
              <span class="text-danger">*</span>
            </label>
            <div class="input-group input-group-merge">
              <span class="input-group-text border-end-0">
                <i class="fas fa-envelope"></i>
              </span>
              <input
                type="email"
                class="form-control border-start-0"
                :class="{ 
                  'is-invalid': v$.email.$error,
                  'is-valid': formData.email && !v$.email.$error
                }"
                id="email"
                v-model="formData.email"
                placeholder="ejemplo@correo.com"
                @focus="fieldFocus = 'email'"
                @blur="fieldFocus = null"
              />
              <div class="invalid-feedback" v-if="v$.email.$error">
                {{ v$.email.$errors[0].$message }}
              </div>
              <div class="valid-feedback" v-if="formData.email && !v$.email.$error">
                ¡Correo electrónico válido!
              </div>
            </div>
            <small 
              class="form-text" 
              :class="{ 'text-muted': fieldFocus !== 'email', 'text-primary': fieldFocus === 'email' }"
            >
              Ingrese un correo electrónico válido
            </small>
          </div>

          <!-- DNI -->
          <div class="form-group mb-4">
            <label for="dni" class="form-label">
              DNI
              <span class="text-muted">(opcional)</span>
            </label>
            <div class="input-group input-group-merge">
              <span class="input-group-text border-end-0">
                <i class="fas fa-id-card"></i>
              </span>
              <input
                type="text"
                class="form-control border-start-0"
                :class="{ 
                  'is-invalid': v$.dni.$error,
                  'is-valid': formData.dni && !v$.dni.$error
                }"
                id="dni"
                v-model="formData.dni"
                placeholder="Ingrese DNI"
                @focus="fieldFocus = 'dni'"
                @blur="fieldFocus = null"
              />
              <div class="invalid-feedback" v-if="v$.dni.$error">
                {{ v$.dni.$errors[0].$message }}
              </div>
            </div>
            <small 
              class="form-text" 
              :class="{ 'text-muted': fieldFocus !== 'dni', 'text-primary': fieldFocus === 'dni' }"
            >
              Si lo desea, puede ingresar el DNI del cliente
            </small>
          </div>

          <!-- Roles -->
          <div class="form-group mb-4">
            <label class="form-label">
              Roles
              <span class="text-muted">(opcional)</span>
            </label>
            <div v-if="loadingRoles" class="text-muted small py-2">
              <span class="spinner-border spinner-border-sm me-1"></span> Cargando roles...
            </div>
            <div v-else-if="availableRoles.length === 0" class="text-muted small py-2">
              No hay roles disponibles. <router-link to="/configuracion/roles" class="text-primary">Crear roles</router-link>
            </div>
            <div v-else class="roles-grid">
              <div
                v-for="role in availableRoles"
                :key="role.id"
                class="role-check"
                :class="{ selected: formData.roles.includes(role.name) }"
                @click="toggleRole(role.name)"
              >
                <i class="fas fa-user-tag me-2"></i>
                {{ role.name }}
                <i v-if="formData.roles.includes(role.name)" class="fas fa-check ms-auto"></i>
              </div>
            </div>
          </div>

          <!-- Contraseña -->
          <div class="form-group mb-4">
            <label for="password" class="form-label">
              Contraseña
              <span class="text-danger">*</span>
            </label>
            <div class="input-group input-group-merge">
              <span class="input-group-text border-end-0">
                <i class="fas fa-lock"></i>
              </span>
              <input
                :type="showPassword ? 'text' : 'password'"
                class="form-control border-start-0"
                :class="{ 
                  'is-invalid': v$.password.$error,
                  'is-valid': formData.password && !v$.password.$error
                }"
                id="password"
                v-model="formData.password"
                placeholder="Ingrese contraseña"
                @focus="fieldFocus = 'password'"
                @blur="fieldFocus = null"
              />
              <button
                type="button"
                class="btn toggle-password-btn"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
              <div class="invalid-feedback" v-if="v$.password.$error">
                {{ v$.password.$errors[0].$message }}
              </div>
              <div class="valid-feedback" v-if="formData.password && !v$.password.$error">
                ¡Contraseña válida!
              </div>
            </div>
            <small 
              class="form-text" 
              :class="{ 'text-muted': fieldFocus !== 'password', 'text-primary': fieldFocus === 'password' }"
            >
              La contraseña debe tener al menos 6 caracteres
            </small>
          </div>

          <!-- Mensaje de error -->
          <div v-if="error" class="alert alert-danger mb-4">
            <div class="d-flex align-items-center">
              <i class="fas fa-exclamation-circle me-2"></i>
              <span>{{ error }}</span>
            </div>
          </div>

          <!-- Botones de acción -->
          <div class="d-flex gap-3 justify-content-end">
            <router-link
              to="/configuracion/clientes"
              class="btn btn-cancel"
              :class="{ disabled: isLoading }"
            >
              <i class="fas fa-times me-2"></i>
              Cancelar
            </router-link>
            <button
              type="submit"
              class="btn btn-submit"
              :disabled="isLoading || !formIsValid"
            >
              <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="fas fa-save me-2"></i>
              {{ isLoading ? 'Creando cuenta...' : 'Crear Cuenta' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, email, minLength, helpers } from '@vuelidate/validators'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { createCustomer } from '@/services/customers'
import { getRoles } from '@/services/rbac'

const router = useRouter()
const toast = useToast()

// Roles
const availableRoles = ref([])
const loadingRoles = ref(false)

onMounted(async () => {
  loadingRoles.value = true
  try {
    availableRoles.value = await getRoles()
  } catch {
    // Non-critical: silently ignore if roles can't be loaded
  } finally {
    loadingRoles.value = false
  }
})

const toggleRole = (roleName) => {
  const idx = formData.roles.indexOf(roleName)
  if (idx === -1) formData.roles.push(roleName)
  else formData.roles.splice(idx, 1)
}

// Estado del formulario
const formData = reactive({
  username: '',
  email: '',
  password: '',
  dni: '',
  roles: []
})

// Estado UI
const showPassword = ref(false)
const isLoading = ref(false)
const error = ref('')
const fieldFocus = ref(null)

// Reglas de validación
const rules = {
  username: { 
    required: helpers.withMessage('El nombre de usuario es requerido', required),
    minLength: helpers.withMessage('El nombre de usuario debe tener al menos 4 caracteres', minLength(4))
  },
  email: { 
    required: helpers.withMessage('El correo electrónico es requerido', required),
    email: helpers.withMessage('Ingrese un correo electrónico válido', email)
  },
  password: { 
    required: helpers.withMessage('La contraseña es requerida', required),
    minLength: helpers.withMessage('La contraseña debe tener al menos 6 caracteres', minLength(6))
  },
  dni: { 
    minLength: helpers.withMessage('El DNI debe tener al menos 7 caracteres', minLength(7))
  }
}

const v$ = useVuelidate(rules, formData)

// Computed properties
const formIsValid = computed(() => {
  return !v$.value.$invalid
})

const formProgress = computed(() => {
  let progress = 0
  const totalFields = 3 // username, email, password (required fields)
  
  if (formData.username && !v$.value.username.$invalid) progress++
  if (formData.email && !v$.value.email.$invalid) progress++
  if (formData.password && !v$.value.password.$invalid) progress++
  
  return Math.round((progress / totalFields) * 100)
})

// Watchers
watch(formData, async () => {
  if (error.value) {
    error.value = ''
  }
}, { deep: true })

// Manejo del submit
const handleSubmit = async () => {
  try {
    error.value = ''
    const isFormValid = await v$.value.$validate()
    
    if (!isFormValid) return
    
    isLoading.value = true
    
    await createCustomer(formData)
    
    toast.success('Cliente creado exitosamente')
    router.push('/configuracion/clientes')
    
  } catch (err) {
    console.error('Error al crear cliente:', err)
    error.value = err.message || 'Error al crear la cuenta del cliente'
    toast.error('Error al crear la cuenta')
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
/* ===== Container ===== */
.create-customer-container {
  margin: 0 auto;
  padding: 0 0.5rem;
}

/* ===== Panel ===== */
.form-panel {
  background: rgba(0,0,0,0.15);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px;
}

.panel-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}

.header-icon { font-size: 1.25rem; color: #555; }

.panel-title {
  color: #ccc;
  font-size: 1.15rem;
  font-weight: 600;
  margin: 0;
  letter-spacing: -0.01em;
}

.panel-subtitle {
  color: #555;
  font-size: 0.82rem;
  margin: 0.2rem 0 0;
}

.panel-body { padding: 1.5rem; }

/* ===== Progress ===== */
.progress {
  background: rgba(255,255,255,0.06);
  border-radius: 6px;
  overflow: hidden;
}

.progress-bar {
  background: var(--primary-color);
  transition: width 0.4s ease;
}
.progress-bar.bg-success { background: var(--success-color) !important; }

.progress-container .text-muted { color: #555 !important; font-size: 0.75rem; }

/* ===== Form Fields ===== */
.form-group label {
  color: #888;
  font-weight: 600;
  font-size: 0.82rem;
  margin-bottom: 0.35rem;
}
.form-group label .text-danger { color: #f87171 !important; }

.input-group-merge .input-group-text {
  background: rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.1);
  color: #555;
  border-radius: 8px 0 0 8px;
  transition: all 0.15s ease;
}

.input-group-merge .form-control {
  transition: all 0.15s ease;
}

.input-group-merge .form-control:focus {
  box-shadow: none;
}

.input-group-merge .form-control:focus ~ .input-group-text,
.input-group-merge:focus-within .input-group-text {
  border-color: rgba(var(--primary-color-rgb), 0.4);
  color: var(--primary-light);
}

.input-group-text {
  border: 1px solid rgba(255,255,255,0.1);
}

.form-control {
  background: rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.1);
  color: #ccc;
  font-size: 0.84rem;
  border-radius: 8px;
  transition: all 0.15s ease;
}
.form-control::placeholder { color: #444; }
.form-control:focus {
  background: rgba(0,0,0,0.35);
  border-color: rgba(var(--primary-color-rgb), 0.4);
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.15);
  color: #ddd;
}

.form-control.is-valid { border-color: rgba(var(--success-color-rgb), 0.4); }
.form-control.is-invalid { border-color: rgba(248,113,113,0.4); }
.form-control.is-valid,
.form-control.is-invalid { background-image: none; }

.valid-feedback { color: var(--success-color); font-size: 0.78rem; }
.invalid-feedback { color: #f87171; font-size: 0.78rem; }

.form-text {
  font-size: 0.78rem;
  margin-top: 0.35rem;
  transition: color 0.15s ease;
  color: #555;
}
.form-text.text-primary { color: var(--primary-light) !important; }
.form-text.text-muted { color: #444 !important; }

/* ===== Roles Grid ===== */
.roles-grid { display: flex; flex-wrap: wrap; gap: 0.4rem; }

.role-check {
  display: flex;
  align-items: center;
  padding: 0.45rem 0.85rem;
  border-radius: 8px;
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.06);
  color: #aaa;
  font-size: 0.84rem;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
}
.role-check:hover { border-color: rgba(var(--primary-color-rgb), 0.3); background: rgba(var(--primary-color-rgb), 0.08); }
.role-check.selected {
  background: rgba(var(--primary-color-rgb), 0.15);
  border-color: rgba(var(--primary-color-rgb), 0.35);
  color: var(--primary-light);
}

/* ===== Toggle Password Button ===== */
.toggle-password-btn {
  background: rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.1);
  color: #555;
  border-radius: 0 8px 8px 0;
  transition: all 0.15s ease;
}
.toggle-password-btn:hover { color: #aaa; background: rgba(0,0,0,0.35); }

/* ===== Action Buttons ===== */
.btn-cancel {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.1);
  color: #888;
  padding: 0.55rem 1.25rem;
  font-size: 0.84rem;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.15s ease;
  text-decoration: none;
}
.btn-cancel:hover { color: #ccc; border-color: rgba(255,255,255,0.15); background: rgba(255,255,255,0.04); }

.btn-submit {
  background: var(--primary-color);
  border: none;
  color: #fff;
  padding: 0.55rem 1.25rem;
  font-size: 0.84rem;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.15s ease;
}
.btn-submit:hover:not(:disabled) { background: var(--primary-dark); }
.btn-submit:disabled { background: #333; color: #555; cursor: not-allowed; }

/* ===== Alert ===== */
.alert-danger {
  background: rgba(248,113,113,0.08);
  border: 1px solid rgba(248,113,113,0.15);
  color: #f87171;
  border-radius: 8px;
  padding: 0.85rem;
  font-size: 0.84rem;
}

/* ===== Spinner ===== */
.spinner-border.text-primary { color: var(--primary-color) !important; }

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .create-customer-container { padding: 0; }
  .panel-header { padding: 1rem; }
  .panel-body { padding: 1rem; }
  .panel-title { font-size: 1.05rem; }

  .d-flex.gap-3 { flex-direction: column-reverse; }
  .btn-cancel, .btn-submit { width: 100%; margin-bottom: 0.4rem; text-align: center; }
}

/* ===== Animations ===== */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
}
.alert-danger { animation: fadeIn 0.2s ease; }
.invalid-feedback, .valid-feedback { animation: fadeIn 0.15s ease; }
</style> 